/**
 * Home = moments feed. Pure card models (unit-tested): live/tonight games from the live
 * feed (`GET /live/games`) and "Classic finishes" from the committed real replays (`GET /games`).
 */
import { team, type GameMeta, type LiveGame } from '@clutch/core';
import { headshotUrl, teamLogoUrl } from '../media/assets';

export type FeedTeam = { tricode: string; name: string; logo?: string; score?: number };
export type FeedStar = { personId: number; name: string; side: 'home' | 'away'; line: string; img?: string };

export type FeedCard = {
  key: string;
  kind: 'live' | 'tonight' | 'final' | 'classic';
  gameId: string;
  away: FeedTeam;
  home: FeedTeam;
  status: string; // "Q3 4:12" / "7:30 PM EDT" / "Final" / "2026 Playoffs · R1 G2"
  startUtc?: string;
  headline?: string; // classics only: "Hawks erased 14 at MSG"
  sub?: string;
  stars: FeedStar[];
  /** Where a classic replay room starts (seq), so the fan lands just before the finish. */
  startAtSeq?: number;
  hero?: boolean;
};

/** Curated from each game's real play-by-play (fixtures/games/*.json; reports/hero-game.md). */
export const CLASSICS: Record<string, { headline: string; sub: string; startAtSeq: number; stars: Omit<FeedStar, 'img'>[] }> = {
  '0042500122': {
    headline: 'Hawks erased 14 at MSG',
    sub: '2026 Playoffs · R1 G2',
    startAtSeq: 498,
    stars: [
      { personId: 203468, name: 'McCollum', side: 'away', line: '32 PTS' },
      { personId: 1628973, name: 'Brunson', side: 'home', line: '29 PTS' },
    ],
  },
  '0022500001': {
    headline: 'Thunder survive Sengun’s 39 in 2OT',
    sub: 'Opening night · Oct 21',
    startAtSeq: 512,
    stars: [
      { personId: 1628983, name: 'Gilgeous-Alexander', side: 'home', line: '35 PTS' },
      { personId: 1630578, name: 'Sengun', side: 'away', line: '39 PTS' },
    ],
  },
  '0022501224': {
    headline: 'Flagg drops 42, Jazz win it in OT',
    sub: 'Dec 15 · Salt Lake City',
    startAtSeq: 527,
    stars: [
      { personId: 1642843, name: 'Flagg', side: 'away', line: '42 PTS' },
      { personId: 1641718, name: 'George', side: 'home', line: '37 PTS' },
    ],
  },
};
export const HERO_GAME = '0042500122';

const feedTeam = (t: { tricode: string; teamId?: number }, score?: number): FeedTeam => ({
  tricode: t.tricode,
  name: safeName(t.tricode),
  ...(teamLogoUrl(t) ? { logo: teamLogoUrl(t)! } : {}),
  ...(score !== undefined ? { score } : {}),
});

function safeName(code: string): string {
  try {
    return team(code).name;
  } catch {
    return code;
  }
}

export function liveCard(g: LiveGame): FeedCard {
  const kind: FeedCard['kind'] = g.status === 'live' ? 'live' : g.status === 'pre' ? 'tonight' : 'final';
  const scored = g.status !== 'pre';
  return {
    key: `live:${g.gameId}`,
    kind,
    gameId: g.gameId,
    away: feedTeam(g.away, scored ? g.away.score : undefined),
    home: feedTeam(g.home, scored ? g.home.score : undefined),
    status: g.detail,
    startUtc: g.startUtc,
    stars: [],
  };
}

/** A margin-and-overtime headline for replays we haven't curated. Built only from the final. */
export function genericHeadline(m: GameMeta): string {
  const homeWon = m.finalHome > m.finalAway;
  const w = safeName(homeWon ? m.home.tricode : m.away.tricode);
  const l = safeName(homeWon ? m.away.tricode : m.home.tricode);
  const margin = Math.abs(m.finalHome - m.finalAway);
  const ot = m.periods > 4 ? (m.periods === 5 ? ' in OT' : ` in ${m.periods - 4}OT`) : '';
  return margin <= 3 ? `${w} edge ${l}${ot}` : `${w} beat ${l}${ot}`;
}

/** Is a finished game a "classic finish"? Close or overtime. Blowouts never make the feed. */
export const isClassic = (m: GameMeta) => Math.abs(m.finalHome - m.finalAway) <= 6 || m.periods > 4;

export function classicCard(m: GameMeta): FeedCard {
  const cur = CLASSICS[m.gameId];
  const src = m.source;
  return {
    key: `classic:${m.gameId}`,
    kind: 'classic',
    gameId: m.gameId,
    away: feedTeam(m.away, m.finalAway),
    home: feedTeam(m.home, m.finalHome),
    status: cur?.sub ?? m.date,
    headline: cur?.headline ?? genericHeadline(m),
    sub: cur?.sub,
    stars: (cur?.stars ?? []).map((s) => ({ ...s, ...(headshotUrl(s.personId, src) ? { img: headshotUrl(s.personId, src)! } : {}) })),
    ...(cur ? { startAtSeq: cur.startAtSeq } : {}),
    ...(m.gameId === HERO_GAME ? { hero: true } : {}),
  };
}

const LIVE_RANK: Record<string, number> = { live: 0, tonight: 1, final: 2 };

/** The feed: live now → tonight → final (from the live feed), then classic finishes (hero first). */
export function buildFeed(live: readonly LiveGame[], replays: readonly GameMeta[]): { live: FeedCard[]; classics: FeedCard[] } {
  const liveCards = live
    .map(liveCard)
    .sort((a, b) => LIVE_RANK[a.kind]! - LIVE_RANK[b.kind]! || (a.startUtc ?? '').localeCompare(b.startUtc ?? ''));
  const classics = replays
    .filter((m) => CLASSICS[m.gameId] || isClassic(m))
    .map(classicCard)
    .sort((a, b) => Number(!!b.hero) - Number(!!a.hero) || Number(!!CLASSICS[b.gameId]) - Number(!!CLASSICS[a.gameId]) || b.gameId.localeCompare(a.gameId));
  return { live: liveCards, classics };
}
